import { NetworkStatus, useQuery } from '@apollo/client';
import { gql } from '__generated__/gql';
import { DealStatusEnumType, SyndicateStatusEnumType } from '__generated__/graphql';
import { FC } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import SidebarLayout, {
  SidebarLayoutContent,
  SidebarLayoutNavigation,
} from 'layouts/SidebarLayout';

import DataTable from 'components/DataTable';
import ErrorMessage from 'components/ErrorMessage';
import { type Statistic, getValue } from 'components/Statistics';

import LoadingIndicator from 'primitives/LoadingIndicator';

import constants from 'utils/constants';

import { DEALS_QUERY } from '../SyndicateDeals';
import GetStartedBanner from './GetStartedBanner';
import UnderReviewBanner from './UnderReviewBanner';

const PORTFOLIO_QUERY = gql(`
  query SyndicatePortfolio($id: ID!) {
    syndicate(id: $id) {
      id
      name
      tagline
      image
      status
      statistics {
        topIndustry
        topStage
        totalRaisedAmount
        totalUnrealizedValue
        totalRealizedValue
        multipleOnInvestment
      }
    }
  }
`);

function SyndicateTitle({
  name,
  tagline,
  image,
}: {
  name: string;
  tagline: string;
  image?: string | null;
}) {
  return (
    <div className="md:flex md:items-center md:justify-between md:space-x-5 rounded-xl py-3 px-2 bg-gray-50">
      <div className="flex items-start space-x-3">
        <div className="flex-shrink-0">
          <div className="relative">
            <img
              className="h-10 w-10 rounded-xl object-cover"
              src={image ?? constants.ALT_AVATAR_URL}
              alt={name ? `${name} logo` : ''}
              width={40}
              height={40}
            />
            <span className="absolute inset-0 rounded-full shadow-inner" aria-hidden="true" />
          </div>
        </div>
        {/*
          Use vertical padding to simulate center alignment when both lines of text are one line,
          but preserve the same layout if the text wraps without making the image jump around.
        */}
        <div className="">
          <h2 className="-mt-1 font-bold leading-7 text-gray-700 truncate text-xl tracking-tight text-balance">
            {name}
          </h2>
          <p className="text-sm text-gray-600 break-words">{tagline || ' '}</p>
        </div>
      </div>
    </div>
  );
}

function PortfolioStatsCard({
  performanceStats,
  portfolioMixStats,
}: {
  performanceStats: Statistic[];
  portfolioMixStats: Statistic[];
}) {
  return (
    <div className="overflow-hidden rounded-2xl border border-gray-200/80 bg-white shadow-sm">
      <section
        className="border-b border-gray-100 scroll-mt-4"
        aria-labelledby="performance-heading"
        id="performance"
      >
        <h3
          id="performance-heading"
          className="px-6 pt-4 text-xs font-semibold uppercase tracking-wider text-gray-500 text-pretty"
        >
          Performance
        </h3>
        <dl className="grid grid-cols-1 gap-0 p-6 pt-3 sm:grid-cols-3">
          {performanceStats.map(stat => (
            <div
              key={stat.label}
              className="flex min-w-0 flex-col py-3 text-center sm:border-x sm:border-gray-100 sm:first:border-x-0 sm:last:border-x-0"
            >
              <dt className="text-sm font-medium text-gray-500 truncate" title={stat.label}>
                {stat.label}
              </dt>
              <dd className="mt-1 flex min-w-0 justify-center text-2xl font-semibold tracking-tight text-gray-900 tabular-nums">
                {getValue(stat.value, stat.type, stat.navigateTo, stat.customValue)}
              </dd>
            </div>
          ))}
        </dl>
      </section>
      <section
        className="bg-gray-50/50 scroll-mt-4"
        aria-labelledby="portfolio-mix-heading"
        id="portfolio-mix"
      >
        <h3
          id="portfolio-mix-heading"
          className="px-6 pt-4 text-xs font-semibold uppercase tracking-wider text-gray-500 text-pretty"
        >
          Portfolio Mix
        </h3>
        <dl className="grid grid-cols-2 gap-0 p-6 pt-3">
          {portfolioMixStats.map(stat => (
            <div key={stat.label} className="flex min-w-0 flex-col text-center">
              <dt className="text-sm font-medium text-gray-500 truncate" title={stat.label}>
                {stat.label}
              </dt>
              <dd className="mt-1 min-w-0 text-lg font-semibold text-gray-900 break-words">
                {getValue(stat.value, stat.type, stat.navigateTo, stat.customValue)}
              </dd>
            </div>
          ))}
        </dl>
      </section>
    </div>
  );
}

const SyndicatePortfolio: FC = () => {
  const navigate = useNavigate();
  const { syndicateId } = useParams<{ syndicateId: string }>() as { syndicateId: string };

  const { loading, error, data, refetch } = useQuery(PORTFOLIO_QUERY, {
    variables: {
      id: syndicateId,
    },
  });

  const shouldFetchDeals = Boolean(
    data &&
      data.syndicate.status !== SyndicateStatusEnumType.UnderReview &&
      data.syndicate.statistics.totalRaisedAmount != null &&
      Number(data.syndicate.statistics.totalRaisedAmount) > 0
  );

  const {
    data: dealsData,
    loading: dealsLoading,
    error: dealsError,
    refetch: refetchDeals,
    fetchMore: fetchMoreDeals,
    variables: dealsVariables,
    networkStatus: dealsNetworkStatus,
  } = useQuery(DEALS_QUERY, {
    skip: !shouldFetchDeals,
    variables: {
      syndicateId,
      limit: 50,
      cursor: undefined,
      filters: {
        status: DealStatusEnumType.Wired,
        text: '',
      },
    },
    notifyOnNetworkStatusChange: true,
  });

  const renderContent = () => {
    if (loading) return <LoadingIndicator />;

    if (error || !data) return <ErrorMessage error={error} refetch={refetch} />;

    if (data.syndicate.status === SyndicateStatusEnumType.UnderReview)
      return (
        <>
          <SyndicateTitle
            name={data.syndicate.name}
            tagline={data.syndicate.tagline}
            image={data.syndicate.image}
          />
          <UnderReviewBanner />
        </>
      );

    if (
      !data.syndicate.statistics.totalRaisedAmount ||
      data.syndicate.statistics.totalRaisedAmount === 0
    ) {
      return (
        <>
          <SyndicateTitle
            name={data.syndicate.name}
            tagline={data.syndicate.tagline}
            image={data.syndicate.image}
          />
          <GetStartedBanner syndicateId={syndicateId} />
        </>
      );
    }

    return (
      <>
        <SyndicateTitle
          name={data.syndicate.name}
          tagline={data.syndicate.tagline}
          image={data.syndicate.image}
        />
        <PortfolioStatsCard
          performanceStats={[
            {
              label: 'Total Raised Amount',
              value: data.syndicate.statistics.totalRaisedAmount,
              type: 'CURRENCY',
            },
            {
              label: 'Total Unrealized Value',
              value: data.syndicate.statistics.totalUnrealizedValue,
              type: 'CURRENCY',
            },
            {
              label: 'Multiple on Investment',
              value: data.syndicate.statistics.multipleOnInvestment,
              type: 'MULTIPLE',
            },
          ]}
          portfolioMixStats={[
            {
              label: 'Top Industry',
              value: data.syndicate.statistics.topIndustry,
              type: 'INDUSTRY',
            },
            {
              label: 'Top Stage',
              value: data.syndicate.statistics.topStage,
              type: 'STAGE',
            },
          ]}
        />
        <section className="mt-8" aria-labelledby="deals-heading">
          {dealsError && <ErrorMessage error={dealsError} refetch={refetchDeals} />}
          {!dealsError && (
            <>
              <div className="mb-4 flex justify-between items-center">
                <h2 className="text-lg font-semibold text-gray-900 text-pretty">All Investments</h2>
                {dealsData?.syndicate?.deals?.pageInfo && (
                  <span className="text-sm text-gray-500 tabular-nums">
                    {dealsData.syndicate.deals.nodes.length} of{' '}
                    {dealsData.syndicate.deals.pageInfo.totalCount} investments
                  </span>
                )}
              </div>
              {dealsLoading && !dealsData ? (
                <LoadingIndicator />
              ) : (
                <DataTable
                  data={dealsData?.syndicate?.deals?.nodes ?? []}
                  onClick={deal => {
                    if (deal.status === 'UNDER_OPS_REVIEW') {
                      navigate(
                        `/syndicate-dashboard/${syndicateId}/deals/create/${deal.id}/under-review`
                      );
                    } else {
                      navigate(`/syndicate-dashboard/${syndicateId}/deals/${deal.id}`);
                    }
                  }}
                  columns={[
                    {
                      label: 'Company',
                      fieldName: 'companyInformation.company',
                      type: 'IMAGE_WITH_NAME',
                    },
                    {
                      label: 'Status',
                      fieldName: 'status',
                      type: 'STATUS',
                    },
                    {
                      label: 'Round Name',
                      fieldName: 'dealTerms.fundingRoundName',
                      type: 'FUNDING_ROUND',
                    },
                    {
                      label: 'Raised',
                      fieldName: 'statistics.totalWiredAmount',
                      type: 'CURRENCY',
                    },
                    {
                      label: 'Unrealized Value',
                      fieldName: 'statistics.totalUnrealizedValue',
                      type: 'CURRENCY',
                    },
                    {
                      label: 'Multiple on Investment',
                      fieldName: 'statistics.multiple',
                      type: 'MULTIPLE',
                    },
                    {
                      label: 'Wired Date',
                      fieldName: 'finalisedAt',
                      type: 'DATE',
                    },
                  ]}
                  hasNextPage={dealsData?.syndicate?.deals?.pageInfo?.hasNextPage ?? false}
                  filterLoading={dealsNetworkStatus === NetworkStatus.setVariables}
                  paginationLoading={dealsLoading}
                  onLoadMore={() =>
                    fetchMoreDeals({
                      variables: {
                        cursor: dealsData?.syndicate?.deals?.pageInfo?.cursor,
                      },
                    })
                  }
                  emptyListTitle="No portfolio data yet"
                  emptyListDescription="Portfolio investments will appear here once available."
                />
              )}
            </>
          )}
        </section>
      </>
    );
  };

  return (
    <SidebarLayout>
      <SidebarLayoutNavigation
        title="Syndicate Portfolio"
        subTitle="Track your syndicate’s investments and performance."
      />
      <SidebarLayoutContent>{renderContent()}</SidebarLayoutContent>
    </SidebarLayout>
  );
};

export default SyndicatePortfolio;
